import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AdPlatform, AdWatchLocation, RewardAdFormat } from '../../types/Ad.api.type';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

/** 위치 한국어 옵션 */
const LOCATION_OPTIONS: { value: AdWatchLocation | undefined; label: string }[] = [
  { value: undefined, label: '전체' },
  { value: 'ANALYSIS_UNLOCK', label: '분석해제' },
  { value: 'DAILY_AD_BONUS', label: '일일광고보너스' },
  { value: 'MILESTONE_AD_BONUS', label: '마일스톤광고보너스' },
  { value: 'VOTE_AD_BONUS', label: '투표광고보너스' },
  { value: 'CREATOR_MILESTONE_AD_BONUS', label: '작성자마일스톤광고보너스' },
  { value: 'ANALYSIS_FREE_VIEW', label: '무료분석조회' },
  { value: 'EXCHANGE', label: '꺼내기' },
  { value: 'ATTENDANCE_AD_BONUS', label: '출석광고보너스' },
  { value: 'ATTENDANCE_STREAK_SAVE', label: '출석유지광고' },
  { value: 'COMMENT_AD_BONUS', label: '댓글광고보너스' },
  { value: 'SURVEY_LIKE_AD_BONUS', label: '설문좋아요광고보너스' },
  { value: 'COMMENT_LIKE_AD_BONUS', label: '댓글좋아요광고보너스' },
  { value: 'WEEKLY_MISSION_AD_BONUS', label: '이번주미션광고보너스' },
];

/** 플랫폼 옵션 */
const PLATFORM_OPTIONS: { value: AdPlatform | undefined; label: string }[] = [
  { value: undefined, label: '전체' },
  { value: 'TOSS', label: 'TOSS' },
  { value: 'WEB', label: 'WEB' },
  { value: 'ANDROID', label: 'ANDROID' },
];

const FORMAT_OPTIONS: { value: RewardAdFormat | undefined; label: string }[] = [
  { value: undefined, label: '전체' },
  { value: 'REWARDED', label: 'R' },
  { value: 'INTERSTITIAL', label: 'F' },
];

/**
 * 광고 시청 히스토리 컴포넌트
 */
export const AdWatchHistory = () => {
  const [page, setPage] = useState(0);
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [userInput, setUserInput] = useState('');
  const [location, setLocation] = useState<AdWatchLocation | undefined>(undefined);
  const [platform, setPlatform] = useState<AdPlatform | undefined>(undefined);
  const [adFormat, setAdFormat] = useState<RewardAdFormat | undefined>(undefined);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const size = 20;

  /** 히스토리 조회 */
  const { data, isLoading } = useQuery({
    queryKey: ['adWatchHistory', page, userId, location, platform, adFormat, startDate, endDate],
    queryFn: () =>
      adApi.getWatchHistory({
        page,
        size,
        userId,
        location,
        platform,
        adFormat,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }).then(res => res.data),
  });

  const handleSearch = () => {
    const parsed = parseInt(userInput, 10);
    setUserId(userInput ? (isNaN(parsed) ? undefined : parsed) : undefined);
    setPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  /** 시간 포맷 */
  const formatDateTime = (dt: string) => {
    const d = new Date(dt);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* 필터 */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">유저 ID</label>
          <div className="flex gap-1">
            <input
              type="text"
              className="border rounded-md px-3 py-1.5 text-sm w-28"
              placeholder="유저 ID"
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <Button size="sm" variant="outline" onClick={handleSearch}>
              검색
            </Button>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">시청 위치</label>
          <select
            className="border rounded-md px-3 py-1.5 text-sm"
            value={location ?? ''}
            onChange={e => {
              setLocation(e.target.value ? e.target.value as AdWatchLocation : undefined);
              setPage(0);
            }}
          >
            {LOCATION_OPTIONS.map(opt => (
              <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">플랫폼</label>
          <select
            className="border rounded-md px-3 py-1.5 text-sm"
            value={platform ?? ''}
            onChange={e => {
              setPlatform(e.target.value ? e.target.value as AdPlatform : undefined);
              setPage(0);
            }}
          >
            {PLATFORM_OPTIONS.map(opt => (
              <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">광고 형식</label>
          <select
            className="border rounded-md px-3 py-1.5 text-sm"
            value={adFormat ?? ''}
            onChange={e => {
              setAdFormat(e.target.value ? e.target.value as RewardAdFormat : undefined);
              setPage(0);
            }}
          >
            {FORMAT_OPTIONS.map(opt => (
              <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">시작일</label>
          <input
            type="date"
            className="border rounded-md px-3 py-1.5 text-sm"
            value={startDate}
            onChange={e => { setStartDate(e.target.value); setPage(0); }}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">종료일</label>
          <input
            type="date"
            className="border rounded-md px-3 py-1.5 text-sm"
            value={endDate}
            onChange={e => { setEndDate(e.target.value); setPage(0); }}
          />
        </div>
      </div>

      {/* 데스크톱: 테이블 */}
      <Card className="overflow-hidden hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">시간</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">유저</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">시청 위치</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">형식</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">토스 응답</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">플랫폼</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">참조 ID</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">로딩 중...</td>
                  </tr>
                ) : (!data?.content || data.content.length === 0) ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">데이터가 없습니다.</td>
                  </tr>
                ) : (
                  data.content.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDateTime(item.regDateTime)}
                      </td>
                      <td className="px-4 py-3">
                        {item.nickname ? (
                          <span className="text-gray-900">{item.nickname}</span>
                        ) : (
                          <span className="text-gray-400">(비회원)</span>
                        )}
                        {item.userId && (
                          <span className="text-xs text-gray-400 ml-1">#{item.userId}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{item.watchLocationLabel}</td>
                      <td className="px-4 py-3 text-gray-600">{item.adFormat ?? '-'}</td>
                      <td className="px-4 py-3">
                        {item.tossAdResponse ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            item.tossAdResponse === 'userEarnedReward' ? 'bg-green-100 text-green-700' :
                            item.tossAdResponse === 'dismissed' ? 'bg-yellow-100 text-yellow-700' :
                            item.tossAdResponse === 'failedToShow' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {item.tossAdResponse}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {item.platform ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            item.platform === 'TOSS' ? 'bg-blue-100 text-blue-700' :
                            item.platform === 'WEB' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {item.platform}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">
                        {item.referenceId ?? '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 모바일: 카드 리스트 */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <div className="py-8 text-center text-gray-500">로딩 중...</div>
        ) : (!data?.content || data.content.length === 0) ? (
          <div className="py-8 text-center text-gray-500">데이터가 없습니다.</div>
        ) : (
          data.content.map(item => (
            <Card key={item.id}>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    {item.nickname ?? '(비회원)'}
                    {item.userId && <span className="text-xs text-gray-400 ml-1">#{item.userId}</span>}
                  </span>
                  {item.platform ? (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      item.platform === 'TOSS' ? 'bg-blue-100 text-blue-700' :
                      item.platform === 'WEB' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {item.platform}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">-</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{item.watchLocationLabel}</span>
                  <div className="flex items-center gap-2">
                    {item.tossAdResponse && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        item.tossAdResponse === 'userEarnedReward' ? 'bg-green-100 text-green-700' :
                        item.tossAdResponse === 'dismissed' ? 'bg-yellow-100 text-yellow-700' :
                        item.tossAdResponse === 'failedToShow' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {item.tossAdResponse}
                      </span>
                    )}
                    {item.referenceId && <span>참조 #{item.referenceId}</span>}
                  </div>
                </div>
                <div className="text-xs text-gray-400">{formatDateTime(item.regDateTime)}</div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* 페이지네이션 */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            이전
          </Button>
          <span className="text-sm text-gray-600">
            {page + 1} / {data.totalPages} ({data.totalElements}건)
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
};
